import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSoyuzClientConfig } from '@spotahome/soyuz/client';

import classNames from 'classnames/bind';

import { Mediator } from '@spotahome/soyuz-mediator';
import { useFiltersContext } from '@spotahome/ui-library';
import JsonLdHomecardListCarousel from '@spotahome/ui-library/src/tenant/components/JsonLdHomecardListCarousel';

import Map from '@spotahome/marketplace-common/src/components/Map';
import SortBy from '@spotahome/marketplace-common/src/components/SortBy';
import Paginator from '@spotahome/marketplace-common/src/components/Paginator';
import HomeCardPlaceHolder from '@spotahome/marketplace-common/src/components/Homecard/HomeCardPlaceHolder';
import HomecardMap from '@spotahome/marketplace-common/src/components/HomecardMap';

import ModuleContainer from '../components/ModuleContainer';
import HomecardGrid from '../components/HomecardGrid';
import SeoText from '../components/SeoText';
import SearchSeoLandingHomecardsIcon from '../components/SearchSeoLandingHomecardsIcon';

import { useSeoCitiesContext } from '../context/SeoCitiesContext';
import { useSeoSearchContext } from '../context/SeoSearchContext';
import { TOGGLE_MAP } from '../common/utils/channels';

import './Homecards.scss';

const EMPTY_LIST_LENGTH = 15;
const MAP_ID = 'themap';
const SPANISH_REGION = [
  'barcelona',
  'bilbao',
  'granada',
  'madrid',
  'seville',
  'valencia',
  'cordoba'
];
const TEXT_CLASS_NAME = 'homecard-text';
const LAST_CLASS_NAME = 'homecard-text--last';

const getTextClassName = (currentCity, isVariant) =>
  SPANISH_REGION.includes(currentCity) || isVariant
    ? `${TEXT_CLASS_NAME} ${LAST_CLASS_NAME}`
    : TEXT_CLASS_NAME;

// Somehow, when parsing modules in the backend, we got a new line character as text, which is empty
const textIsEmpty = text => !text || text === '\n';
const Homecards = ({
  title,
  text,
  highlighted = false,
  deviceType,
  preloadedHomecards
}) => {
  const [showMap, setShowMap] = useState(false);
  const { currentCity } = useSeoCitiesContext();
  const { defaultZoom } = useSoyuzClientConfig();
  const { filters, handleUpdateFilters } = useFiltersContext();
  const {
    homecards,
    currentMarkers,
    paginator,
    isLoadingHomecards,
    locationCoordinates,
    cityId,
    setMapBoundariesFilter
  } = useSeoSearchContext();

  const isDesktop = deviceType === 'desktop';

  const seoTextClasses = classNames(
    getTextClassName(currentCity, isDesktop),
    'homecards-seo-text'
  );

  useEffect(() => {
    const onToggleMap = () => {
      setShowMap(!showMap);
    };

    Mediator.subscribe(TOGGLE_MAP, onToggleMap);

    return () => {
      Mediator.unsubscribe(TOGGLE_MAP, onToggleMap);
    };
  }, [showMap]);

  const handleFiltersUpdate = sortBy => {
    handleUpdateFilters({
      ...filters,
      sortBy
    });
  };

  const handlePageClick = page => {
    paginator.setCurrentPage(page);
  };

  if (showMap && locationCoordinates) {
    return (
      <div className="homecards__map">
        <Map
          mapId={MAP_ID}
          currencyIsoCode={homecards[0]?.currencyIsoCode}
          centerCoordinates={locationCoordinates}
          defaultZoom={defaultZoom[currentCity]}
          markerList={currentMarkers}
          onMarkerClicked={() => {}}
          onMapDidMount={() => {}}
          onMapWillUnmount={() => {}}
          onIdleFilter={setMapBoundariesFilter}
          onZoomChange={() => {}}
          showPrice
          isMapLoadDelayed
        />
        <HomecardMap cityId={cityId} deviceType={deviceType} context="seo" />
      </div>
    );
  }

  if (!homecards?.length) {
    return null;
  }

  return (
    <ModuleContainer highlighted={highlighted}>
      <SortBy
        className="homecards__sort-by"
        handleSortByChange={handleFiltersUpdate}
        orderBy={filters.sortBy}
      />

      {isLoadingHomecards ? (
        <div className="l-list">
          {new Array(EMPTY_LIST_LENGTH).fill().map((_, i) => (
            <div className="l-list__item" key={i}>
              <HomeCardPlaceHolder />
            </div>
          ))}
        </div>
      ) : (
        <HomecardGrid
          homecards={homecards}
          preloadedHomecards={preloadedHomecards}
        />
      )}

      <div className="homecards__paginator">
        <Paginator
          previousButtonDisabled={paginator.isPrevButtonDisabled}
          nextButtonDisabled={paginator.isNextButtonDisabled}
          onPreviousButtonClick={paginator.handlePrevPage}
          onNextButtonClick={paginator.handleNextPage}
          currentPage={paginator.getCurrentPageNumber()}
          totalPages={paginator.getTotalPages()}
          prevPage={paginator.getPreviousPageNumber()}
          nextPage={paginator.getNextPageNumber()}
          onPageClick={handlePageClick}
        />
        <h2 className="homecards-subtitle">{title}</h2>
      </div>

      {textIsEmpty(text) ? null : (
        <SeoText className={seoTextClasses} text={text} />
      )}
      <SearchSeoLandingHomecardsIcon className="homecards-icon" />
      <JsonLdHomecardListCarousel homecards={homecards.filter(Boolean)} />
    </ModuleContainer>
  );
};

Homecards.propTypes = {
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  highlighted: PropTypes.bool,
  deviceType: PropTypes.string.isRequired,
  preloadedHomecards: PropTypes.arrayOf(PropTypes.string).isRequired
};

export default Homecards;
